import axios, { AxiosInstance } from 'axios';
import { isObject } from 'lodash-es';

import { IBaseResponse, IRequestParams } from '@shared/utils/http/interface';
import { A1_SERVER_URL } from '@shared/env';
import { getRequestCache } from './cache';

import { resFulfillIntcpt, resRejectIntcpt } from './interceptors/response';
import { reqFulfillIntcpt, reqRejectIntcpt } from './interceptors/request';
import { LSK } from '../storage/config';
import { getI18nSetting } from '@shared/i18n/route';
import { distinguishClientType } from '../platform/platformDetection';
import { appOrigin } from '@shared/matrix/const';
import { isMatrixAppOrigin } from '@shared/matrix/util';

const baseUrl = A1_LOCAL ? A1_SERVER_URL : '/';

const config = {
	baseUrl,
	timeout: 20000,
};

export const HTTP_CLIENT_TIMEOUT_MS = config.timeout;

const instance: AxiosInstance = axios.create({
	baseURL: config.baseUrl,
	timeout: config.timeout,
	withCredentials: true, // 跨域请求时发送Cookie
	headers: {
		'Content-Type': 'application/json; charset=UTF-8;',
		'X-Client-type': distinguishClientType(),
		'X-Language': getI18nSetting(),
		'X-App-Version':
			localStorage.getItem(LSK.APP_VERSION) || window.AndroidInterface?.getAppVersion?.() || '',
		// 测试工具可通过 localStorage 覆盖 X-App-Origin（用于切换 appOrigin）
		'X-App-Origin': (() => {
			const storedAppOrigin = localStorage.getItem(LSK.APP_ORIGIN_FOR_TEST);
			if (storedAppOrigin && isMatrixAppOrigin(storedAppOrigin)) {
				return storedAppOrigin;
			}
			return appOrigin;
		})(),
		// Language: getI18nSetting(),
	},
});

instance.interceptors.request.use(reqFulfillIntcpt, reqRejectIntcpt);
instance.interceptors.response.use(resFulfillIntcpt, resRejectIntcpt);

// get请求
export const get = async <T>({ url = '', data, headers = {}, config = {} }: IRequestParams) => {
	if (config.cache) {
		const cache = await getRequestCache({ url, data });
		if (isObject(cache)) {
			return cache as IBaseResponse<T>;
		}
	}

	return new Promise((resolve: (value: IBaseResponse<T>) => void, reject) => {
		instance
			.get(url, Object.assign({}, data ? { params: data } : {}, { ...config, headers }))
			.then((response: any) => {
				resolve(response);
			})
			.catch((err) => {
				reject(err);
			});
	});
};

// post请求
export const post = <T>({ url = '', data, headers = {}, config = {} }: IRequestParams) => {
	return new Promise((resolve: (value: IBaseResponse<T>) => void, reject) => {
		instance.post(url, data, { ...config, headers }).then(
			(response: any) => {
				resolve(response);
			},
			(err) => {
				reject(err);
			},
		);
	});
};

// patch 请求
export const patch = <T>({ url = '', data, headers = {}, config = {} }: IRequestParams) => {
	return new Promise((resolve: (value: IBaseResponse<T>) => void, reject) => {
		instance.patch(url, data, { ...config, headers }).then(
			(response: any) => {
				resolve(response);
			},
			(err) => {
				reject(err);
			},
		);
	});
};

// delete 请求
export const del = <T>({ url = '', headers = {}, config = {} }: IRequestParams) => {
	return new Promise((resolve: (value: IBaseResponse<T>) => void, reject) => {
		instance.delete(url, { ...config, headers }).then(
			(response: any) => {
				resolve(response);
			},
			(err) => {
				reject(err);
			},
		);
	});
};

export const HTTP = {
	get,
	post,
	patch,
	del,
};
